import type { ChainQuote, PinEdge } from "./optionsChain.types";
import {
  formatGreek,
  formatImpliedVolatility,
  formatInteger,
  formatPrice,
  formatSignedPercent,
  formatSignedPrice,
} from "./optionsChain.utils";

/**
 * Canonical default column order, left to right on the calls side and mirrored
 * on the puts side. The picker can reorder the scrollable set; bid and ask stay
 * pinned beside the strike.
 *
 * Bid and ask are excluded here because they are not scrollable — see
 * `STICKY_METRIC_IDS`.
 */
export const SCROLLABLE_METRIC_IDS = [
  "last",
  "netChange",
  "changePercent",
  "volume",
  "openInterest",
  "impliedVolatility",
  "delta",
  "gamma",
  "theta",
  "vega",
  "rho",
] as const;

/**
 * Pinned beside the strike column and never scrolled away, because they are the
 * only actionable cells in the grid.
 */
export const STICKY_METRIC_IDS = ["bid", "ask"] as const;

export const CHAIN_METRIC_IDS = [
  ...SCROLLABLE_METRIC_IDS,
  ...STICKY_METRIC_IDS,
] as const;

export type ChainMetricId = (typeof CHAIN_METRIC_IDS)[number];

/** The five Greeks. Implied volatility is deliberately not one of them. */
export const GREEK_METRIC_IDS = [
  "delta",
  "gamma",
  "theta",
  "vega",
  "rho",
] as const;

/** How a metric arrives, which decides its freshness treatment. */
export type MetricTransport =
  /** Quote fields: can be current even on the delayed feed. */
  | "quote"
  /** Trade and volume: 15 minutes behind unless the account is on OPRA. */
  | "delayed"
  /** Computed per request, never streamed. */
  | "computed"
  /** Daily batch from the contracts endpoint, two trading days behind. */
  | "endOfDay";

export interface ChainMetric {
  id: ChainMetricId;
  label: string;
  /** Used in the narrow fit, where headers have to be terse. */
  shortLabel: string;
  transport: MetricTransport;
  /** Shown until the user turns it off in the column picker. */
  isDefault: boolean;
  /** Quote metrics render as clickable buttons that build an order leg. */
  isQuoteAction?: boolean;
  /** Pinned beside the strike column rather than scrolled. */
  isSticky?: boolean;
  /** Kept in the narrowest fit. */
  isEssential?: boolean;
  widthRem: number;
  format: (quote: ChainQuote) => string;
  /** The same figure unformatted, used to tell which way a value moved. */
  value: (quote: ChainQuote) => number | undefined;
  /** Rendered green when positive and red when negative. */
  isSigned?: boolean;
}

/**
 * Wide enough for a four-digit premium, which is the ceiling in practice: a
 * premium only reaches five digits if the underlying trades above $10,000, and
 * no optionable US equity does.
 */
const QUOTE_WIDTH_REM = 4.75;

export const CHAIN_METRICS: Record<ChainMetricId, ChainMetric> = {
  last: {
    id: "last",
    label: "Last",
    shortLabel: "Last",
    transport: "delayed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.last,
    format: (quote) => formatPrice(quote.last),
  },
  netChange: {
    id: "netChange",
    label: "Change",
    shortLabel: "Change",
    transport: "delayed",
    isDefault: false,
    widthRem: 5,
    isSigned: true,
    value: (quote) => quote.netChange,
    format: (quote) => formatSignedPrice(quote.netChange),
  },
  changePercent: {
    id: "changePercent",
    label: "Change (%)",
    shortLabel: "Change (%)",
    transport: "delayed",
    isDefault: false,
    widthRem: 5,
    isSigned: true,
    value: (quote) => quote.changePercent,
    format: (quote) => formatSignedPercent(quote.changePercent),
  },
  volume: {
    id: "volume",
    label: "Volume",
    shortLabel: "Vol",
    transport: "delayed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.volume,
    format: (quote) => formatInteger(quote.volume),
  },
  openInterest: {
    id: "openInterest",
    label: "Open interest",
    shortLabel: "OI",
    transport: "endOfDay",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.openInterest,
    format: (quote) => formatInteger(quote.openInterest),
  },
  impliedVolatility: {
    id: "impliedVolatility",
    label: "Implied volatility",
    shortLabel: "IV",
    transport: "computed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.impliedVolatility,
    format: (quote) => formatImpliedVolatility(quote.impliedVolatility),
  },
  delta: {
    id: "delta",
    label: "Delta",
    shortLabel: "Delta",
    transport: "computed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.greeks?.delta,
    format: (quote) => formatGreek(quote.greeks?.delta),
  },
  gamma: {
    id: "gamma",
    label: "Gamma",
    shortLabel: "Gamma",
    transport: "computed",
    isDefault: true,
    widthRem: 5,
    value: (quote) => quote.greeks?.gamma,
    format: (quote) => formatGreek(quote.greeks?.gamma, 4),
  },
  theta: {
    id: "theta",
    label: "Theta",
    shortLabel: "Theta",
    transport: "computed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.greeks?.theta,
    format: (quote) => formatGreek(quote.greeks?.theta),
  },
  vega: {
    id: "vega",
    label: "Vega",
    shortLabel: "Vega",
    transport: "computed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.greeks?.vega,
    format: (quote) => formatGreek(quote.greeks?.vega),
  },
  rho: {
    id: "rho",
    label: "Rho",
    shortLabel: "Rho",
    transport: "computed",
    isDefault: true,
    widthRem: 4.5,
    value: (quote) => quote.greeks?.rho,
    format: (quote) => formatGreek(quote.greeks?.rho),
  },
  bid: {
    id: "bid",
    label: "Bid",
    shortLabel: "Bid",
    transport: "quote",
    isDefault: true,
    isQuoteAction: true,
    isSticky: true,
    isEssential: true,
    widthRem: QUOTE_WIDTH_REM,
    // A zero bid is "nobody is bidding", not a price of zero. Saying so keeps a
    // user from reading 0.00 as tradable, and from computing a mid off one side.
    value: (quote) => quote.bid,
    format: (quote) =>
      quote.bid === undefined ? "No bid" : formatPrice(quote.bid),
  },
  ask: {
    id: "ask",
    label: "Ask",
    shortLabel: "Ask",
    transport: "quote",
    isDefault: true,
    isQuoteAction: true,
    isSticky: true,
    isEssential: true,
    widthRem: QUOTE_WIDTH_REM,
    value: (quote) => quote.ask,
    format: (quote) => formatPrice(quote.ask),
  },
};

export const DEFAULT_METRIC_IDS: readonly ChainMetricId[] =
  CHAIN_METRIC_IDS.filter((id) => CHAIN_METRICS[id].isDefault);

export const isChainMetricId = (value: unknown): value is ChainMetricId =>
  typeof value === "string" &&
  (CHAIN_METRIC_IDS as readonly string[]).includes(value);

export const defaultMetricIds = (): ChainMetricId[] => [...DEFAULT_METRIC_IDS];

/** Bid and ask are the actionable cells, so they are never optional. */
export const isMetricRequired = (id: ChainMetricId) =>
  !!CHAIN_METRICS[id].isSticky;

/** Drop side for a dragged column, matching the Orders picker. */
export type MetricDropDirection = "before" | "after";

/**
 * Fills any missing scrollable ids after the ones the user already ordered, so
 * a stale store cannot hide a column from the picker.
 */
export const completeMetricOrder = (
  order: readonly ChainMetricId[],
): ChainMetricId[] => {
  const seen = new Set<ChainMetricId>();
  const next: ChainMetricId[] = [];
  for (const id of order) {
    if (!isChainMetricId(id) || isMetricRequired(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    next.push(id);
  }
  for (const id of SCROLLABLE_METRIC_IDS) {
    if (!seen.has(id)) {
      next.push(id);
    }
  }
  return next;
};

export const reorderScrollableMetrics = (
  order: readonly ChainMetricId[],
  source: ChainMetricId,
  target: ChainMetricId,
  direction: MetricDropDirection,
): ChainMetricId[] => {
  if (
    source === target ||
    isMetricRequired(source) ||
    isMetricRequired(target)
  ) {
    return completeMetricOrder(order);
  }

  const next = completeMetricOrder(order);
  const fromIndex = next.indexOf(source);
  const toIndex = next.indexOf(target);
  if (fromIndex < 0 || toIndex < 0) {
    return next;
  }

  const [removed] = next.splice(fromIndex, 1);
  const targetIndex = next.indexOf(target);
  if (targetIndex < 0) {
    return completeMetricOrder(order);
  }
  next.splice(
    direction === "after" ? targetIndex + 1 : targetIndex,
    0,
    removed,
  );
  return next;
};

export interface VisibleMetrics {
  /** Rendered in the horizontally scrolling area, in the user's order. */
  scrollable: ChainMetric[];
  /** Pinned beside the strike column: bid then ask. */
  sticky: ChainMetric[];
}

/**
 * Resolves which metrics render. Scrollable columns follow `metricOrder` (and
 * fall back to the registry). Bid and ask always pin beside the strike.
 *
 * `narrow` is a runtime fit measured from the widget's own width — it reduces the
 * user's choice for this render only and never rewrites the stored preference.
 */
export const resolveVisibleMetrics = ({
  enabledMetricIds,
  metricOrder = SCROLLABLE_METRIC_IDS,
  isNarrow,
}: {
  enabledMetricIds: readonly ChainMetricId[];
  metricOrder?: readonly ChainMetricId[];
  isNarrow: boolean;
}): VisibleMetrics => {
  const enabled = new Set(enabledMetricIds);

  const sticky = STICKY_METRIC_IDS.map((id) => CHAIN_METRICS[id]);

  if (isNarrow) {
    return { scrollable: [], sticky };
  }

  const scrollable = completeMetricOrder(metricOrder)
    .filter((id) => enabled.has(id))
    .map((id) => CHAIN_METRICS[id]);

  return { scrollable, sticky };
};

/** Sum of widths for one side, used as that pane's horizontal min width. */
export const sideMinWidthRem = (metrics: VisibleMetrics) =>
  [...metrics.scrollable, ...metrics.sticky].reduce(
    (total, metric) => total + metric.widthRem,
    0,
  );

/**
 * Offset from the pinned edge for a sticky column, in rem. Bid sits outside ask
 * on the calls side and inside it on the puts side, so both stay adjacent to the
 * strike column.
 */
export const stickyOffsetRem = (
  metrics: readonly ChainMetric[],
  index: number,
  edge: PinEdge,
): number =>
  edge === "right"
    ? metrics.slice(index + 1).reduce((total, m) => total + m.widthRem, 0)
    : metrics.slice(0, index).reduce((total, m) => total + m.widthRem, 0);
